/**
 * Initializer Module for Murphy Door Configurator
 *
 * Manages complete initialization process through distinct phases:
 * 1. Data Initialization (Hybrid Bootstrap Architecture)
 *    Phase 1A: Extracts Liquid data from DOM (config + products) - synchronous
 *    Phase 1B: Fetches metaobjects via Storefront API (DataFetcher) - parallel async
 *    Phase 1C: Merges data and creates PPD via unified pipeline
 *    - Caches PPD for instant next visit (Instagram optimization)
 *    - Provides PPD to conductor (legacy processedData removed)
 *
 * 2. Structure Initialization
 *    - Loads categories and tabs
 *    - Restores saved configurations
 *    - Updates progress states
 *
 * 3. UI Initialization
 *    - Sets up DOM elements
 *    - Establishes event listeners
 *    - Configures initial display states
 *
 * PERFORMANCE ARCHITECTURE:
 * - Liquid complexity reduced from 50,000+ to ~500 (no metaobject loops)
 * - Metaobjects fetched via Storefront API in parallel (~400-800ms)
 * - Pagination support for 1000+ rules (no 250 item limit)
 * - Built-in throttling prevents Shopify API overload
 *
 * Dependencies:
 * - md-config-support-data-fetcher.js (Storefront API metaobject fetching)
 * - md-config-core-data-pipeline.js (unified pipeline for PPD creation)
 * - md-config-core-conductor.js (receives PPD for orchestration)
 *
 * This module serves as the primary initialization coordinator for the entire configurator system.
 */
(function (window) {
  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};

  // Dev-only logging (production guard)
  const isDev = () =>
    window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  const initializer = {
    _isInitialized: false,
    _bootstrapComplete: false, // Phase 1 runtime protection flag
    _productId: null, // Store product ID for cache key uniqueness

    async initialize() {
      // 🛡️ INITIALIZATION GUARD: Prevent multiple initialization attempts
      if (this._isInitialized) {
        isDev() &&
          console.warn(
            '🛡️ INITIALIZER: Already initialized - skipping duplicate execution'
          );
        return;
      }

      if (!window.MurphyDoorConfigurator.main) {
        // Wait for main module to be available
        setTimeout(() => this.initialize(), 50);
        return;
      }

      // Extract product ID early for product-specific caching
      try {
        const productDataEl = document.getElementById(
          'murphy-door-product-data'
        );
        if (productDataEl) {
          const productData = JSON.parse(productDataEl.textContent);
          this._productId = productData.id;
          isDev() &&
            console.log(
              `🎯 INITIALIZER: Product ID extracted for cache: ${this._productId}`
            );
        } else {
          isDev() &&
            console.warn(
              '⚠️ INITIALIZER: Product ID not found, caching will be disabled'
            );
        }
      } catch (e) {
        isDev() &&
          console.warn(
            '⚠️ INITIALIZER: Failed to extract product ID for caching:',
            e
          );
      }

      // Mark as initialized before calling to prevent race conditions
      this._isInitialized = true;
      isDev() &&
        console.log(
          '🎯 INITIALIZER: Starting configurator initialization with new data flow'
        );

      // 🔧 PHASE 0: Wait for critical module dependencies
      isDev() &&
        console.log('🔧 INITIALIZER: Waiting for module dependencies...');
      const criticalModules = ['supportVariantEnhancer'];
      const allModulesReady = await this.waitForModuleDependencies(
        criticalModules,
        5000
      );

      if (!allModulesReady) {
        isDev() &&
          console.warn(
            '⚠️ INITIALIZER: One or more critical modules failed to load within timeout'
          );
      }

      isDev() &&
        console.log(
          '[CoreInitializer] Phase 1 module loaded and ready for data extraction'
        );

      // ARCHITECTURAL FIX: Start with data extraction first
      this.initializeDataPhase();
    },

    /**
     * Hybrid Bootstrap Architecture - Complete Data Initialization
     *
     * Phase 1A: Extract Liquid data from DOM (config + products) - synchronous
     * Phase 1B: Fetch metaobjects via Storefront API (DataFetcher) - parallel async
     * Phase 1C: Merge data and create PPD via unified 4-layer pipeline
     *
     * DATA FLOW:
     * 1. Check PPD cache (instant load on return visits)
     * 2. Extract Liquid data from DOM (config + products)
     * 3. Fetch ALL metaobjects via Storefront API (parallel, paginated)
     * 4. Merge Liquid data + fetched metaobjects
     * 5. Process through 4-layer unified pipeline to create PPD
     * 6. Cache PPD for instant next visit
     * 7. Provide PPD to conductor
     */
    async initializeDataPhase() {
      // ============================================================================
      // CRITICAL: BOOTSTRAP-ONLY RUNTIME PROTECTION
      // 🛡️ GLOBAL SINGLETON GUARD v2: DUAL CHECK — flag + PPD existence.
      // Flag-only check fails when the FIRST bootstrap ran old code (no flag guard).
      // PPD existence is version-agnostic: old code always creates the PPD as a result,
      // so if it exists we know bootstrap already ran regardless of which code version ran.
      // Global flag handles the concurrent-start race condition (set before async work).
      // ============================================================================
      // window.__murphyDoorBootstrapComplete is on raw window (not MurphyDoorConfigurator namespace)
      // so it survives even if the namespace is wiped or recreated during async section rendering.
      if (
        window.__murphyDoorBootstrapComplete ||
        window.MurphyDoorConfigurator._bootstrapPipelineComplete ||
        window.MurphyDoorConfigurator.perfectProductDictionary
      ) {
        isDev() &&
          console.log(
            '🛡️ BOOTSTRAP: Already complete (window flag / PPD / namespace flag) - skipping duplicate run'
          );
        return;
      }
      // Mark BOTH locations BEFORE any async work to prevent concurrent duplicate runs
      window.__murphyDoorBootstrapComplete = true;
      window.MurphyDoorConfigurator._bootstrapPipelineComplete = true;

      isDev() &&
        console.log('🏗️ INITIALIZER: Phase 1 - Hybrid Bootstrap Architecture');

      try {
        // INSTAGRAM OPTIMIZATION: Check cache FIRST for instant load on return visits!
        const cacheResult = await this.checkPPDCache();
        if (cacheResult.ppd) {
          isDev() &&
            console.log(
              '⚡ CACHE HIT: Skipping pipeline + API fetch, using cached PPD!'
            );
          window.MurphyDoorConfigurator.conductor.perfectProductDictionary =
            cacheResult.ppd;
          window.MurphyDoorConfigurator.perfectProductDictionary =
            Object.freeze(cacheResult.ppd);

          // ✅ PHASE 1 REFACTOR: Dispatch DOM CustomEvent for bootstrap completion
          const bootstrapEvent = new CustomEvent(
            'murphyDoorBootstrapComplete',
            {
              detail: {
                ppd: cacheResult.ppd,
                timestamp: Date.now(),
                source: 'initializer',
                fromCache: true,
              },
            }
          );
          performance.mark('md:bootstrap-complete');
          document.dispatchEvent(bootstrapEvent);
          isDev() &&
            console.log(
              '✅ BOOTSTRAP: murphyDoorBootstrapComplete event dispatched (cache path)'
            );

          // Skip to initialization - PPD ready instantly!
          isDev() &&
            console.log(
              '✅ CACHE: Bootstrap complete via cache, starting configurator'
            );
          this._bootstrapComplete = true;
          // Global flag already set at top of initializeDataPhase() before any async work
          isDev() &&
            console.log(
              '🎯 Initializer: Bootstrap complete - runtime protection enabled'
            );
          window.MurphyDoorConfigurator.main.initializeConfigurator();
          return;
        }

        isDev() &&
          console.log(
            '💾 CACHE MISS: Building PPD via hybrid bootstrap (Liquid + API)'
          );

        // ═══════════════════════════════════════════════════════
        // Phase 2B: Try CDN pre-built PPD (server-generated)
        // ═══════════════════════════════════════════════════════
        let cdnPpd = await this.tryFetchPrebuiltPpd();
        if (cdnPpd) {
          isDev() && console.log('🚀 CDN HIT: Using server-generated PPD!');

          // Phase 2B: Detect lean PPD and rebuild stripped HTML caches
          if (cdnPpd.__ppdMeta && cdnPpd.__ppdMeta.htmlMapsStripped) {
            isDev() &&
              console.log(
                '🔧 CDN: Lean PPD detected — rebuilding HTML caches...'
              );
            try {
              cdnPpd =
                window.MurphyDoorConfigurator.dataPipeline.rebuildHtmlCaches(
                  cdnPpd
                );
            } catch (rebuildError) {
              isDev() &&
                console.error(
                  '❌ CDN: HTML cache rebuild failed, falling back to client pipeline:',
                  rebuildError
                );
              cdnPpd = null;
            }
          }

          if (cdnPpd) {
            window.MurphyDoorConfigurator.conductor.perfectProductDictionary =
              cdnPpd;
            window.MurphyDoorConfigurator.perfectProductDictionary =
              Object.freeze(cdnPpd);

            const bootstrapEvent = new CustomEvent(
              'murphyDoorBootstrapComplete',
              {
                detail: {
                  ppd: cdnPpd,
                  timestamp: Date.now(),
                  source: 'initializer',
                  fromCache: false,
                  fromCdn: true,
                },
              }
            );
            performance.mark('md:bootstrap-complete');
            document.dispatchEvent(bootstrapEvent);
            isDev() &&
              console.log(
                '✅ BOOTSTRAP: murphyDoorBootstrapComplete event dispatched (CDN path)'
              );

            // Cache FULL PPD (with rebuilt HTML maps) to IDB — return visits skip rebuild
            this.cachePPD(cdnPpd, null);

            this._bootstrapComplete = true;
            isDev() &&
              console.log(
                '🎯 Initializer: Bootstrap complete via CDN - runtime protection enabled'
              );
            window.MurphyDoorConfigurator.main.initializeConfigurator();
            return;
          }
        }

        isDev() &&
          console.log('📡 CDN MISS: Falling back to client-side pipeline');

        // ═══════════════════════════════════════════════════════
        // Phase 1A & 1B: Extract DOM data and fetch API data in PARALLEL
        // ═══════════════════════════════════════════════════════
        // PERFORMANCE OPTIMIZATION: Run independent operations concurrently
        // Saves ~50-100ms by parallelizing DOM extraction + API fetch
        const [extractedData, metaobjects] = await Promise.all([
          cacheResult.rawData
            ? Promise.resolve(cacheResult.rawData)
            : this.extractConfigurationData(),
          this.fetchMetaobjects(),
        ]);
        const rawData = extractedData;

        if (cacheResult.rawData) {
          isDev() &&
            console.log(
              '♻️ OPTIMIZATION: Reused rawData from cache validation + parallel API fetch'
            );
        } else {
          isDev() &&
            console.log(
              '📊 OPTIMIZATION: Parallel DOM extraction + API fetch complete'
            );
        }

        // ═══════════════════════════════════════════════════════
        // Phase 1C: Merge Liquid data + fetched metaobjects
        // ═══════════════════════════════════════════════════════
        isDev() &&
          console.log(
            '🔗 INITIALIZER: Phase 1C - Merging Liquid data + API metaobjects'
          );
        rawData.woodMetaobjects = metaobjects.woodMetaobjects;
        rawData.finishMetaobjects = metaobjects.finishMetaobjects;
        rawData.forbiddenConditionGroups = metaobjects.forbiddenConditionGroups;
        rawData.forbiddenRulesUnified = metaobjects.forbiddenRulesUnified;

        isDev() &&
          console.log('✅ INITIALIZER: Merged dataset ready for pipeline', {
            products: rawData.productData?.add_on_products?.length || 0,
            woods: rawData.woodMetaobjects.length,
            finishes: rawData.finishMetaobjects.length,
            conditionGroups: rawData.forbiddenConditionGroups.length,
            unifiedRules: rawData.forbiddenRulesUnified.length,
          });

        // Step 2: Create Perfect Product Dictionary via unified pipeline
        isDev() &&
          console.log(
            '🎯 INITIALIZER: Creating perfectProductDictionary via unified pipeline'
          );
        const ppd =
          window.MurphyDoorConfigurator.dataPipeline.createPerfectProductDictionary(
            rawData
          );

        // 🔍 DEBUG: Log Map sizes after receiving from data-pipeline
        isDev() &&
          console.log(
            '🔍 DEBUG TRACE: PPD HTML Maps after createPerfectProductDictionary:',
            {
              productToVariantLayoutHTMLMap_size:
                ppd.productToVariantLayoutHTMLMap?.size,
              productToVariantLayoutHTMLMap_isMap:
                ppd.productToVariantLayoutHTMLMap instanceof Map,
              compositeProductToVariantGridHTMLMap_size:
                ppd.compositeProductToVariantGridHTMLMap?.size,
            }
          );

        // INSTAGRAM OPTIMIZATION: Cache PPD for instant next visit!
        // Fire-and-forget: caching is non-blocking, don't delay bootstrap for it
        this.cachePPD(ppd, rawData);

        // Step 3: Provide PPD as sole data source to conductor
        isDev() &&
          console.log(
            '🎯 INITIALIZER: Providing PPD to conductor (legacy processedData removed)'
          );
        window.MurphyDoorConfigurator.conductor.perfectProductDictionary = ppd;

        // 🔍 DEBUG: Log Map sizes after assigning to conductor
        isDev() &&
          console.log(
            '🔍 DEBUG TRACE: PPD HTML Maps after conductor assignment:',
            {
              conductor_productToVariantLayoutHTMLMap_size:
                window.MurphyDoorConfigurator.conductor.perfectProductDictionary
                  .productToVariantLayoutHTMLMap?.size,
              conductor_isMap:
                window.MurphyDoorConfigurator.conductor.perfectProductDictionary
                  .productToVariantLayoutHTMLMap instanceof Map,
            }
          );
        // Expose PPD on the app namespace for debugging and freeze it, making it
        // read-only for the entire application (including the conductor's reference).
        window.MurphyDoorConfigurator.perfectProductDictionary =
          Object.freeze(ppd);

        // ✅ PHASE 1 REFACTOR: Dispatch DOM CustomEvent for bootstrap completion
        const bootstrapEvent = new CustomEvent('murphyDoorBootstrapComplete', {
          detail: {
            ppd: ppd,
            timestamp: Date.now(),
            source: 'initializer',
            fromCache: false,
          },
        });
        performance.mark('md:bootstrap-complete');
        document.dispatchEvent(bootstrapEvent);
        isDev() &&
          console.log(
            '✅ BOOTSTRAP: murphyDoorBootstrapComplete event dispatched (fresh build path)'
          );

        // Step 4: Continue with traditional initialization flow
        isDev() &&
          console.log(
            '✅ INITIALIZER: Bootstrap complete, starting main configurator initialization'
          );
        this._bootstrapComplete = true;
        isDev() &&
          console.log(
            '🎯 Initializer: Bootstrap complete - runtime protection enabled'
          );
        window.MurphyDoorConfigurator.main.initializeConfigurator();
      } catch (error) {
        isDev() &&
          console.error(
            '❌ INITIALIZER: Data initialization phase failed:',
            error
          );
        throw error;
      }
    },

    /**
     * Phase 1A: Extract Liquid-provided data from DOM
     * Only extracts data that comes from Liquid (config, products, media).
     * Metaobjects are fetched separately via Storefront API (Phase 1B).
     *
     * @returns {Object} Raw Liquid data from DOM script elements
     */
    extractConfigurationData() {
      // ============================================================================
      // CRITICAL: BOOTSTRAP-ONLY RUNTIME PROTECTION
      // ============================================================================
      if (this._bootstrapComplete) {
        const error =
          'CRITICAL ARCHITECTURAL VIOLATION: initializer.extractConfigurationData() called AFTER bootstrap complete! ' +
          'This is a Phase 1 Bootstrap method for DOM data extraction. ' +
          'Runtime Phase 2 modules MUST use PPD data via conductor delegation instead of re-extracting from DOM.';
        isDev() && console.error(error, new Error().stack);
        throw new Error(error);
      }

      isDev() &&
        console.log(
          '🏗️ INITIALIZER: Phase 1A - Extracting Liquid data from DOM'
        );

      try {
        // ⚡ FAST PATH: Use pre-parsed data from early inline script (eliminates JSON.parse at pipeline start)
        // The Liquid template pre-parses all JSON data blocks before module scripts load,
        // so by the time we get here the data is already in memory as JS objects.
        const preParsed = window.__ppdRawDataReady;
        let configData, productData, mediaSlideData;

        if (preParsed && preParsed.productData && preParsed.configData) {
          configData = preParsed.configData;
          productData = preParsed.productData;
          mediaSlideData = preParsed.mediaSlideData || { media: [] };
          isDev() &&
            console.log(
              '⚡ INITIALIZER: Phase 1A — using pre-parsed data (saved JSON.parse of ~1.4MB)'
            );
        } else {
          // Fallback: parse from DOM (handles edge cases where pre-parse failed)
          configData = JSON.parse(
            document.getElementById('murphy-door-config-data').textContent
          );
          productData = JSON.parse(
            document.getElementById('murphy-door-product-data').textContent
          );
          mediaSlideData = JSON.parse(
            document.getElementById('murphy-door-media-slide-data')
              ?.textContent || '{"media":[]}'
          );
          isDev() &&
            console.log(
              '📋 INITIALIZER: Phase 1A — parsed from DOM (pre-parse not available)'
            );
        }

        isDev() &&
          console.log(
            '✅ INITIALIZER: Phase 1A DOM data extraction successful'
          );
        isDev() &&
          console.log(
            `📊 Products: ${productData.add_on_products?.length || 0}, Config tabs: ${configData.configuratorTabs?.length || 0}, Media slides: ${mediaSlideData.media?.length || 0}`
          );

        return {
          configData,
          productData,
          // Metaobject placeholders - will be populated by Phase 1B (DataFetcher)
          woodMetaobjects: [],
          finishMetaobjects: [],
          forbiddenConditionGroups: [],
          forbiddenRulesUnified: [],
          mediaSlideData,
          timestamp: performance.now(),
        };
      } catch (error) {
        isDev() &&
          console.error(
            '❌ INITIALIZER: Failed to extract configuration data from DOM:',
            error
          );
        throw new Error(`DOM data extraction failed: ${error.message}`);
      }
    },

    /**
     * Can a cached metaobject snapshot EXPRESS the contract the pipeline reads?
     *
     * Metaobjects reach the configurator through a snapshot in
     * `shop.metafields.custom.md_configurator_metaobjects_cache`, written by a
     * backend service. When the pipeline starts consuming a NEW metaobject field,
     * a writer that predates that field keeps emitting the old shape: four arrays
     * present, structurally valid, every existing check green -- and the new field
     * absent on every row. The pipeline then silently skips whatever behaviour the
     * field drives.
     *
     * 🚨 This is not hypothetical. Measured on production 2026-09-03:
     * `expand_from_product` was set on 63 of 66 condition groups in the Admin API
     * and present on ZERO of them in this snapshot. Forbidden-rule expansion
     * therefore never ran, rules under-matched, and a forbidden combination was
     * selectable and cart-ready -- with no error, no warning, and no failed
     * request. The snapshot also still carried 8 variants that had been deleted in
     * a finish swap, so it was wrong in both directions at once.
     *
     * Shape validity is not enough. `expand_from_product` is optional per group, so
     * its absence on ONE row means nothing -- but its absence on EVERY row means
     * the writer does not know the field exists, i.e. the snapshot predates the
     * contract. In that case ignore it and pay the ~508ms live fetch, which is
     * correct-but-slower instead of fast-but-silently-wrong.
     *
     * @param {Object} m - Candidate snapshot
     * @returns {boolean} true if the snapshot may be trusted
     */
    snapshotExpressesCurrentContract(m) {
      const groups = m && m.forbiddenConditionGroups;
      // An empty set carries no evidence either way; let the caller proceed.
      if (!Array.isArray(groups) || groups.length === 0) return true;
      return groups.some(
        (g) =>
          g && Object.prototype.hasOwnProperty.call(g, 'expand_from_product')
      );
    },

    /**
     * Does a CACHED perfectProductDictionary still express the contract the
     * code consuming it expects?
     *
     * Sibling of snapshotExpressesCurrentContract() above, same doctrine and
     * deliberately the same shape: when the cache cannot be vouched for, ignore
     * it and pay a rebuild, because correct-but-slower beats
     * fast-but-silently-wrong.
     *
     * 🚨 WHY THIS IS NEEDED AT ALL. checkPPDCache() validates
     * productVersion/globalVersion (webhook-driven, and they move only when
     * Shopify CONTENT changes), a catalog hash of add-on ids, and a 7-day TTL.
     * None of those notice a THEME JS DEPLOY. So a pipeline change that alters
     * the Layer 5 maps invalidates nothing, and every visitor holding an older
     * PPD is served the old shape, unvalidated, for up to a week. The symptom
     * is "Cache error - please refresh" -- advice that cannot work, since a
     * refresh revalidates the same entry against the same unchanged version.
     *
     * Measured on production 2026-09-04 (theme 132070506541): deleting one
     * entry from productToVariantLayoutHTMLMap for Custom Jamb Depth (133
     * variants) made showVariantLayout render the error string.
     *
     * THE EVIDENCE RULE, inherited from the metaobject check. A single product
     * missing from the layout map means nothing -- plenty of products
     * legitimately have no grid. But if NOT ONE of a sample of genuinely
     * multi-variant products resolves, the writer of this cache did not agree
     * with this reader about how the map is keyed or populated, i.e. the cache
     * predates the contract. That is exactly the shape of 66694dfe3
     * (2026-02-04), where standalone ids were not normalized to strings and
     * .set()/.get() mismatched -- live 5 days on Wood & Finish.
     *
     * What this deliberately does NOT catch: content staleness. A cached tile
     * reading "$600.00" after whole-dollar stripping shipped resolves fine and
     * passes every check here. That is what CONSTANTS.PPD_CONTRACT_VERSION is
     * for; the two defences are complements, not alternatives.
     *
     * @param {Object} ppd - Candidate cached PPD
     * @returns {boolean} true if the cache may be trusted
     */
    ppdExpressesCurrentContract(ppd) {
      if (!ppd) return false;

      // 1. STRUCTURAL. Every Layer 5 map this build consumes must be present
      //    and an actual Map. IndexedDB's structured clone preserves Maps
      //    natively, so one arriving as a plain object means something upstream
      //    changed shape -- rebuild rather than rehydrate it.
      const requiredMaps = [
        'products',
        'productToVariantLayoutHTMLMap',
        'compositeProductToVariantGridHTMLMap',
        'productToDirectVariantLayoutHTMLMap',
      ];
      for (let i = 0; i < requiredMaps.length; i += 1) {
        if (!(ppd[requiredMaps[i]] instanceof Map)) {
          isDev() &&
            console.warn(
              `[Initializer] Cached PPD is missing Map "${requiredMaps[i]}" - rebuilding.`
            );
          return false;
        }
      }

      // 2. EVIDENCE. Sample multi-variant products and require that at least
      //    one resolves in the layout map. Bounded on purpose: this runs on the
      //    bootstrap path, and a full catalog scan to answer a yes/no question
      //    would be its own regression.
      const SAMPLE_LIMIT = 10;
      let sampled = 0;
      let resolved = 0;

      const productIterator = ppd.products.values();
      let next = productIterator.next();
      while (!next.done && sampled < SAMPLE_LIMIT) {
        const product = next.value;
        const variantCount =
          product && product.variants ? product.variants.length : 0;
        // Mirror the population predicate in md-config-core-data-pipeline.js:
        // only products with more than one variant ever get a cached grid.
        if (variantCount > 1 && product.type !== 'CompositeProduct') {
          sampled += 1;
          if (ppd.productToVariantLayoutHTMLMap.has(product.id)) resolved += 1;
        }
        next = productIterator.next();
      }

      // No multi-variant products sampled carries no evidence either way --
      // let the caller proceed, exactly as the metaobject check does for an
      // empty group array.
      if (sampled === 0) return true;

      if (resolved === 0) {
        isDev() &&
          console.warn(
            `[Initializer] Cached PPD resolved 0 of ${sampled} sampled multi-variant ` +
              'products in productToVariantLayoutHTMLMap - it predates the current ' +
              'contract (key or population change). Rebuilding.'
          );
        return false;
      }

      return true;
    },

    /**
     * Phase 1B: Fetch metaobjects via Storefront API
     * Uses DataFetcher for paginated, parallel fetching of all 4 metaobject types.
     *
     * @returns {Promise<Object>} Fetched metaobject data { woodMetaobjects, finishMetaobjects, forbiddenConditionGroups, forbiddenRulesUnified }
     */
    async fetchMetaobjects() {
      // ⚡ FASTEST PATH: Use pre-parsed metaobjects from early inline script
      const preParsed = window.__ppdRawDataReady;
      if (preParsed && preParsed.metaobjects) {
        const m = preParsed.metaobjects;
        if (
          Array.isArray(m.woodMetaobjects) &&
          Array.isArray(m.finishMetaobjects) &&
          Array.isArray(m.forbiddenConditionGroups) &&
          Array.isArray(m.forbiddenRulesUnified)
        ) {
          isDev() &&
            console.log(
              '⚡ METAOBJECTS: Pre-parsed cache hit — skipping DOM lookup + JSON.parse' +
                ' (woods: ' +
                m.woodMetaobjects.length +
                ', finishes: ' +
                m.finishMetaobjects.length +
                ', conditions: ' +
                m.forbiddenConditionGroups.length +
                ', rules: ' +
                m.forbiddenRulesUnified.length +
                ')'
            );
          if (this.snapshotExpressesCurrentContract(m)) return m;
          isDev() &&
            console.warn(
              '[Initializer] Pre-parsed metaobject snapshot predates the ' +
                'expand_from_product contract — ignoring it and fetching live.'
            );
        }
      }

      // ⚡ FAST PATH: Use inline metaobjects if Liquid injected them (eliminates ~508ms)
      const inlineEl = document.getElementById('md-metaobjects-static');
      if (inlineEl) {
        try {
          const inlineData = JSON.parse(inlineEl.textContent);
          if (
            Array.isArray(inlineData?.woodMetaobjects) &&
            Array.isArray(inlineData?.finishMetaobjects) &&
            Array.isArray(inlineData?.forbiddenConditionGroups) &&
            Array.isArray(inlineData?.forbiddenRulesUnified)
          ) {
            isDev() &&
              console.log(
                '⚡ METAOBJECTS: Inline cache hit — skipping Storefront API fetch' +
                  ' (woods: ' +
                  inlineData.woodMetaobjects.length +
                  ', finishes: ' +
                  inlineData.finishMetaobjects.length +
                  ', conditions: ' +
                  inlineData.forbiddenConditionGroups.length +
                  ', rules: ' +
                  inlineData.forbiddenRulesUnified.length +
                  ')'
              );
            if (this.snapshotExpressesCurrentContract(inlineData))
              return inlineData;
            isDev() &&
              console.warn(
                '[Initializer] Inline metaobject snapshot predates the ' +
                  'expand_from_product contract — ignoring it and fetching live.'
              );
          }
        } catch (e) {
          // Parse failed — fall through to API fetch
          isDev() &&
            console.warn(
              '[Initializer] Inline metaobjects parse failed, falling back to API',
              e
            );
        }
      }

      // SLOW PATH: Storefront API fetch (unchanged)
      isDev() &&
        console.log(
          '🏗️ INITIALIZER: Phase 1B - Fetching metaobjects via Storefront API'
        );
      const startTime = performance.now();

      try {
        // Get storefront token from DOM
        const tokenElement = document.getElementById(
          'murphy-door-storefront-token'
        );
        const storefrontToken = tokenElement
          ? JSON.parse(tokenElement.textContent)
          : '';

        if (!storefrontToken) {
          isDev() &&
            console.error(
              '❌ INITIALIZER: No Storefront Access Token found in DOM'
            );
          throw new Error('Storefront Access Token not found');
        }

        // Determine shop domain
        const hostname = window.location.hostname;
        const shopDomain =
          hostname.includes('127.0.0.1') || hostname.includes('localhost')
            ? 'murphydoor.myshopify.com'
            : hostname;

        // Wait for DataFetcher class to be available
        const DataFetcherClass = window.MurphyDoorConfigurator.DataFetcher;
        if (!DataFetcherClass) {
          throw new Error(
            'DataFetcher class not available - ensure md-config-support-data-fetcher.js is loaded'
          );
        }

        const fetcher = new DataFetcherClass(storefrontToken, shopDomain);
        const metaobjects = await fetcher.fetchAllMetaobjects();

        // ARCHITECTURAL FIX: No DOM injection needed - data flows directly through rawData
        // to the data pipeline via parameter passing (clean data flow, no DOM intermediate)

        const duration = performance.now() - startTime;
        isDev() &&
          console.log(
            `✅ INITIALIZER: Phase 1B complete - metaobjects fetched in ${duration.toFixed(1)}ms`,
            {
              woods: metaobjects.woodMetaobjects.length,
              finishes: metaobjects.finishMetaobjects.length,
              conditionGroups: metaobjects.forbiddenConditionGroups.length,
              unifiedRules: metaobjects.forbiddenRulesUnified.length,
            }
          );

        return metaobjects;
      } catch (error) {
        isDev() &&
          console.error(
            '❌ INITIALIZER: Phase 1B metaobject fetch failed:',
            error
          );
        isDev() &&
          console.error(
            '🛑 CRITICAL: Cannot proceed without forbidden rules - halting initialization'
          );

        // TODO: Display user-facing error message when error handler is available
        // const errorMessage = 'We could not load essential configuration data. Please refresh the page.';
        // window.MurphyDoorConfigurator.errorHandler?.displayFatalError(errorMessage);

        throw new Error(
          'Failed to load critical metaobject data. Cannot initialize configurator.'
        );
      }
    },

    /**
     * INSTAGRAM OPTIMIZATION: Check IndexedDB for cached PPD
     *
     * Performance Strategy:
     * - Cache Hit: ~5ms bootstrap (IDB read only, skips entire pipeline)
     * - Cache Miss: ~60ms bootstrap (full 4-layer pipeline + cache write)
     * - Cache validation via product catalog hash (detects catalog changes)
     * - Version-based invalidation via webhook-driven PPD fingerprints
     * - Product-specific keys for multi-product support
     *
     * Result: Instant configurator load on repeat visits! ⚡
     *
     * Cache Validation Chain:
     * 1. Check if cache exists for this product ID in IndexedDB
     * 2. Validate cache structure (ppd, hash, timestamp)
     * 3. Validate PPD not empty (productsProcessed > 0)
     * 4. Version-based invalidation (productVersion, globalVersion)
     * 5. Hash safety net (catches edge cases webhooks miss)
     * 6. 7-day absolute safety TTL
     * 7. Return cached PPD if valid, or rawData for reuse if invalid
     *
     * Smart Data Reuse: On hash mismatch, returns extracted rawData to prevent
     * double-extraction by caller (PPD-First Data Reuse pattern).
     *
     * IndexedDB Advantages over localStorage:
     * - No 5-10MB size limit (supports large PPDs with Maps)
     * - Structured clone preserves Maps natively (no JSON.stringify needed)
     * - Non-blocking async I/O (doesn't block main thread)
     *
     * @returns {Promise<{ ppd: Object|null, rawData: Object|null }>} Cache result object
     *   - ppd: Cached PPD if valid, null if cache miss
     *   - rawData: Extracted data if available (on hash mismatch), null otherwise
     */

    /**
     * Phase 2B: Try fetching a pre-built PPD from S3/CDN.
     *
     * Server-side PPD generation pre-builds the dictionary and uploads it to S3.
     * This method fetches it, deserializes Maps/Sets, and validates the shape.
     *
     * Falls back gracefully — any error returns null (client pipeline takes over).
     *
     * @returns {Promise<Object|null>} Deserialized PPD or null on any failure
     */
    async tryFetchPrebuiltPpd() {
      try {
        // Read CDN base URL from Liquid-injected config
        const cdnBaseUrl = window.MurphyDoorConfigurator._ppdCdnBaseUrl;
        if (!cdnBaseUrl) {
          isDev() &&
            console.log('📡 CDN: No CDN base URL configured, skipping');
          return null;
        }

        // Get product handle for the CDN URL path
        const productDataEl = document.getElementById(
          'murphy-door-product-data'
        );
        if (!productDataEl) return null;
        const productData = JSON.parse(productDataEl.textContent);
        const productHandle = productData.handle;
        if (!productHandle) return null;

        // Build the CDN URL for latest PPD
        const cdnUrl =
          cdnBaseUrl.replace(/\/$/, '') +
          '/' +
          productHandle +
          '/ppd-latest.json';
        isDev() && console.log('📡 CDN: Fetching pre-built PPD from', cdnUrl);

        // Fetch with 3-second timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(function () {
          controller.abort();
        }, 3000);

        const response = await fetch(cdnUrl, {
          signal: controller.signal,
          cache: 'default',
        });
        clearTimeout(timeoutId);

        if (!response.ok) {
          isDev() &&
            console.log(
              '📡 CDN: HTTP ' +
                response.status +
                ' - no pre-built PPD available'
            );
          return null;
        }

        const jsonString = await response.text();

        // Deserialize with Map/Set reconstruction
        if (!window.MurphyDoorConfigurator.ppdDeserializer) {
          isDev() &&
            console.warn(
              '📡 CDN: ppdDeserializer not loaded, cannot parse CDN PPD'
            );
          return null;
        }

        const ppd =
          window.MurphyDoorConfigurator.ppdDeserializer.parse(jsonString);

        // Validate PPD shape — must have products Map and metadata
        if (!ppd || !(ppd.products instanceof Map) || ppd.products.size === 0) {
          isDev() &&
            console.warn('📡 CDN: Invalid PPD shape (no products Map)');
          return null;
        }

        isDev() &&
          console.log(
            '✅ CDN: Pre-built PPD loaded! ' +
              ppd.products.size +
              ' products, ' +
              (ppd.categories instanceof Map ? ppd.categories.size : 0) +
              ' categories'
          );

        return ppd;
      } catch (err) {
        // AbortError = timeout, TypeError = network error — all graceful fallback
        isDev() &&
          console.log(
            '📡 CDN: Fetch failed (' +
              (err.name || 'Error') +
              '), falling back to client pipeline'
          );
        return null;
      }
    },

    async checkPPDCache() {
      // 🔧 DEV MODE: Disable cache on localhost for easier development
      if (
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1'
      ) {
        isDev() &&
          console.log(
            '🔧 DEV MODE: Cache disabled on localhost for development'
          );
        return { ppd: null, rawData: null };
      }

      // Disable cache if no product ID (can't create unique key)
      if (!this._productId) {
        isDev() && console.log('💾 CACHE: Disabled (no product ID)');
        return { ppd: null, rawData: null };
      }

      // Disable cache if IDB cache API not available
      if (!window.MurphyDoorConfigurator.idbCache) {
        isDev() &&
          console.warn(
            '⚠️ CACHE: IndexedDB cache API not available, skipping cache check'
          );
        return { ppd: null, rawData: null };
      }

      try {
        const cachedData = await window.MurphyDoorConfigurator.idbCache.get(
          this._productId
        );

        if (!cachedData) return { ppd: null, rawData: null };

        // Validate cache structure before destructuring
        if (!cachedData.ppd || !cachedData.timestamp) {
          isDev() &&
            console.warn('⚠️ CACHE: Invalid cache structure, clearing');
          await window.MurphyDoorConfigurator.idbCache.delete(this._productId);
          return { ppd: null, rawData: null };
        }

        const { ppd, hash, timestamp } = cachedData;

        // ✅ PHASE 1+2 REFACTOR: Validate PPD isn't empty before using cache
        const productCount = ppd?.metadata?.productsProcessed || 0;
        if (productCount === 0) {
          isDev() &&
            console.warn(
              '⚠️ CACHE: Empty PPD detected (0 products), invalidating cache'
            );
          await window.MurphyDoorConfigurator.idbCache.delete(this._productId);
          return { ppd: null, rawData: null };
        }

        // ✅ PPD FINGERPRINT: Version-based cache invalidation (replaces 24h TTL)
        // Webhook-driven versions from Super App: product changes bump product version,
        // metaobject/collection changes bump global version. Both are written to Shopify
        // metafields and output by Liquid in #murphy-door-product-data JSON.
        const rawData = this.extractConfigurationData();
        const currentProductVersion =
          rawData.productData.ppd_product_version || 0;
        const currentGlobalVersion =
          rawData.productData.ppd_global_version || 0;
        const cachedProductVersion = cachedData.productVersion || 0;
        const cachedGlobalVersion = cachedData.globalVersion || 0;

        if (
          currentProductVersion !== cachedProductVersion ||
          currentGlobalVersion !== cachedGlobalVersion
        ) {
          isDev() &&
            console.log(
              `🔄 CACHE: PPD version changed (product: ${cachedProductVersion}→${currentProductVersion}, global: ${cachedGlobalVersion}→${currentGlobalVersion}), invalidating`
            );
          await window.MurphyDoorConfigurator.idbCache.delete(this._productId);
          return { ppd: null, rawData: rawData };
        }

        // Safety net: hash check catches edge cases webhooks might miss
        const currentHash = this.generateProductHashFromData(rawData);
        if (hash && currentHash !== hash) {
          isDev() &&
            console.log('🔄 CACHE: Product catalog hash changed, invalidating');
          await window.MurphyDoorConfigurator.idbCache.delete(this._productId);
          return { ppd: null, rawData: rawData };
        }

        // 7-day maximum TTL as absolute safety net (prevents infinitely stale cache)
        const cacheAge = Date.now() - timestamp;
        const maxAge = 7 * 24 * 60 * 60 * 1000; // 7 days
        if (cacheAge > maxAge) {
          isDev() &&
            console.log('🗑️ CACHE: Expired (>7 days safety TTL), clearing');
          await window.MurphyDoorConfigurator.idbCache.delete(this._productId);
          return { ppd: null, rawData: rawData };
        }

        // CONTRACT VERSION: the only signal that moves on a THEME DEPLOY.
        // Everything checked above tracks Shopify CONTENT, so none of it
        // notices that the code baking this cache has changed. Bumped by hand;
        // see CONSTANTS.PPD_CONTRACT_VERSION for why it cannot be automatic.
        const currentContractVersion =
          window.MurphyDoorConfigurator?.CONSTANTS?.PPD_CONTRACT_VERSION ?? 0;
        const cachedContractVersion = cachedData.contractVersion ?? 0;
        if (currentContractVersion !== cachedContractVersion) {
          isDev() &&
            console.log(
              `🔄 CACHE: PPD contract version changed (${cachedContractVersion}→${currentContractVersion}), invalidating`
            );
          await window.MurphyDoorConfigurator.idbCache.delete(this._productId);
          return { ppd: null, rawData: rawData };
        }

        // CONTRACT SHAPE: catches the regressions nobody remembered to bump for.
        // Runs last because it is the most expensive check here -- everything
        // above is an O(1) comparison, so an already-doomed cache never reaches
        // this. Fail-safe: an internal error means "cannot vouch for it", never
        // "trust it", because this gates the bootstrap of every configurator
        // PDP and a throw would take the whole page down.
        let contractOk = false;
        try {
          contractOk = this.ppdExpressesCurrentContract(ppd);
        } catch (contractError) {
          isDev() &&
            console.warn(
              '⚠️ CACHE: contract check threw, treating cache as untrustworthy:',
              contractError
            );
          contractOk = false;
        }
        if (!contractOk) {
          await window.MurphyDoorConfigurator.idbCache.delete(this._productId);
          return { ppd: null, rawData: rawData };
        }

        isDev() &&
          console.log(
            `⚡ CACHE HIT: Valid cache (v${currentProductVersion}/${currentGlobalVersion}, ${(cacheAge / 1000 / 60).toFixed(0)}min old)`
          );
        return { ppd: ppd, rawData: null }; // rawData not needed on cache hit
      } catch (error) {
        isDev() && console.warn('⚠️ CACHE: Read failed, rebuilding:', error);
        return { ppd: null, rawData: null };
      }
    },

    /**
     * INSTAGRAM OPTIMIZATION: Cache PPD to IndexedDB for instant next visit
     *
     * Caching Strategy:
     * - Product-specific keys (supports multiple products per site)
     * - Version-based invalidation (webhook-driven from Super App)
     * - Hash-based validation as safety net (detects catalog changes)
     * - 7-day maximum TTL as absolute safety net
     * - Dev mode bypass (disabled on localhost for easier development)
     *
     * IndexedDB Advantages over localStorage:
     * - No practical size limit (localStorage caps at 5-10MB)
     * - Structured clone preserves Maps natively (no JSON.stringify needed!)
     * - Non-blocking async I/O (doesn't block main thread during write)
     *
     * Performance Impact:
     * - First visit: Pays ~3ms cache write cost (async, non-blocking)
     * - Return visits: Saves ~55-100ms by skipping entire 4-layer pipeline
     * - ROI: 18-33x performance gain on repeat visits
     *
     * Cache Structure (stored via structured clone, Maps preserved):
     * {
     *   ppd: Object,            // Complete Perfect Product Dictionary (Maps intact!)
     *   productVersion: number, // Webhook-driven product version
     *   globalVersion: number,  // Webhook-driven global version
     *   hash: string,           // Product catalog hash for validation
     *   timestamp: number       // Cache creation time for expiry
     * }
     *
     * @param {Object} ppd - Perfect Product Dictionary to cache
     * @param {Object} rawData - Pre-extracted raw data for hash generation (eliminates DOM re-read)
     */
    async cachePPD(ppd, rawData) {
      // 🔧 DEV MODE: Disable cache on localhost for easier development
      if (
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1'
      ) {
        isDev() &&
          console.log(
            '🔧 DEV MODE: Cache disabled on localhost for development'
          );
        return;
      }

      // Disable cache if no product ID (can't create unique key)
      if (!this._productId) {
        isDev() && console.log('💾 CACHE: Disabled (no product ID)');
        return;
      }

      // Disable cache if IDB cache API not available
      if (!window.MurphyDoorConfigurator.idbCache) {
        isDev() &&
          console.warn(
            '⚠️ CACHE: IndexedDB cache API not available, skipping cache write'
          );
        return;
      }

      try {
        // ✅ OPTIMIZATION: Use pre-extracted rawData for hash (null when source is CDN)
        const hash = this.generateProductHashFromData(rawData);

        const cacheData = {
          ppd,
          hash,
          // rawData is null for CDN-sourced PPDs — fall back to ppd.metadata versions
          productVersion:
            rawData?.productData?.ppd_product_version ??
            ppd.metadata?.productVersion ??
            0,
          globalVersion:
            rawData?.productData?.ppd_global_version ??
            ppd.metadata?.globalVersion ??
            0,
          // Stamp the contract version this PPD was BUILT by, so a later read
          // can tell it apart from one built by different code. Must stay in
          // step with the comparison in checkPPDCache(): a value written but
          // never read (or read but never written) fails open and silently
          // does nothing, which is the same class of bug this whole change
          // exists to close.
          contractVersion:
            window.MurphyDoorConfigurator?.CONSTANTS?.PPD_CONTRACT_VERSION ?? 0,
          timestamp: Date.now(),
        };

        isDev() &&
          console.log(
            '💾 CACHE: Storing PPD to IndexedDB (structured clone preserves Maps)'
          );

        await window.MurphyDoorConfigurator.idbCache.set(
          this._productId,
          cacheData
        );
        isDev() &&
          console.log(
            '✅ CACHE: PPD cached successfully to IndexedDB for next visit!'
          );
        // Signal IDB write completion — used by prewarm iframe to safely remove itself
        document.dispatchEvent(
          new CustomEvent('murphyDoorIdbWriteComplete', {
            detail: { productId: this._productId },
          })
        );
      } catch (error) {
        isDev() && console.warn('⚠️ CACHE: Write failed (non-fatal):', error);
        // Non-fatal, just means no caching for this session
      }
    },

    /**
     * Cache-validation fingerprint over the PPD's VOLATILE COMMERCIAL FIELDS.
     *
     * Pure function that accepts rawData instead of re-reading DOM.
     * This eliminates redundant DOM extraction during cache validation.
     *
     * 🚨 WHY THIS COVERS PRICE AND AVAILABILITY, not just ids.
     * Until 2026-09-09 this hashed `add_on_products` **ids only**. That made it a
     * MEMBERSHIP check: it saw an add-on joining or leaving the collection and was
     * blind to a re-price, an out-of-stock flip, a rename, or the base product's
     * own variants. Harmless while `recoverState()` destroyed the IDB cache on
     * every clean-URL load (PPD entries measured 1 -> 0 -> 0 across three loads),
     * because everyone rebuilt anyway. `2d13d12e0` removed that call and the cache
     * now persists to its 7-day TTL, so the blindness became a real one-week
     * staleness window on prices customers configure against.
     *
     * The super-app side cannot cover this. Its per-product invalidation path
     * (`ppd_product_version`) is dead — measured 2026-09-09: 0 of 250 products
     * carry a value, the storefront reads `0`, and the `products/*` webhook that
     * would bump it has never been subscribed in the entire git history. And it
     * could not cover it even if wired, because it bumps the CHANGED product's
     * metafield while this theme reads the BASE product's — an add-on re-price
     * would bump a metafield nothing reads. A content fingerprint is the correct
     * mechanism: it moves exactly when the data the PPD is built from moves, and
     * never on the ~473 daily `products/update` events that touch nothing here.
     *
     * WHAT IS HASHED (measured on the live 1MB payload, 149 add-ons / 1,383 variants):
     * - per add-on product: id + product-level `price`
     * - per add-on variant:  id + `price` + `available`
     * - per BASE product variant: id + `price` + `available`
     * Entries are sorted by id, so a Liquid array reorder does NOT bust the cache.
     *
     * WHAT IS DELIBERATELY NOT HASHED — a decision, not an oversight. Titles,
     * images, finishes/woods and `production_time` are excluded: a stale CDN image
     * URL still resolves and a stale title is cosmetic, whereas price/availability
     * are transactional. Every field added is a field whose CHURN is unmeasured,
     * and unbounded churn would bust the cache this file exists to keep. Price
     * churn IS measured (5 `APO_PRICE_CHANGED` events in 7 days), which is why
     * these two are in and the rest are not. Add a field only with its churn.
     *
     * 🪤 `available` uses a THREE-state token ('1'/'0'/'?'). An absent flag must
     * not collapse into `false`, or "we never observed it" reads as "out of stock"
     * and a genuine restock would not move the hash.
     *
     * 🚨 THE SORT IS LOAD-BEARING — do NOT remove it to save time. All three
     * configurator add-on collections are `sortOrder = BEST_SELLING` (measured
     * 2026-09-09: 150 / 203 / 319 products), so Liquid's array order changes
     * SPONTANEOUSLY as sales accrue, with no merchant action and no content
     * change. An order-dependent digest would therefore false-bust every
     * visitor's cache repeatedly — destroying the cache `2d13d12e0` just enabled.
     * Sorting by id is what makes reorder-vs-re-price distinguishable.
     *
     * 🪤 A cheaper order-independent form was TRIED AND REJECTED: summing a
     * per-entry rolling hash needs no sort and ran ~2x faster, but this hash is
     * linear in charcodes, so the sum lets the price terms COMMUTE — swapping two
     * variants' prices (a real merchant action: "I mixed these two up") produced
     * an IDENTICAL digest. Verified against the live payload with genuinely
     * different prices (179.00 <-> 199.00), not equal ones. Do not reintroduce it.
     *
     * Performance, measured against the real 1MB production payload in one run
     * (not estimated): warm median 0.068ms -> 0.699ms, i.e. +0.63ms. Cold
     * first-call — which is what a page load actually pays, since this runs ONCE
     * — measured 0.26-0.71ms -> 2.0-5.5ms in a fresh V8 process. A browser with
     * the JSON already parsed sits at the low end. Either way it is single-digit
     * milliseconds guarding a 55-100ms rebuild, so the trade is clearly worth it.
     * Do not "optimise" it back into a membership check.
     *
     * 🔑 The `v2_` prefix makes the format change SELF-INVALIDATING: an entry
     * cached as "149_294884670" cannot equal "v2_149_...", so every warm cache
     * rebuilds exactly once on deploy. No CONSTANTS.PPD_CONTRACT_VERSION bump is
     * needed for that, and bumping it would be a redundant second bust.
     *
     * @param {Object} rawData - Raw configuration data from extractConfigurationData()
     * @param {Object} rawData.productData - Product data containing add_on_products + variants
     * @returns {string} Fingerprint "v2_{count}_{hash}", or 'unknown'/'error' when it cannot be built
     */
    generateProductHashFromData(rawData) {
      try {
        const productData = rawData?.productData;
        if (!productData) {
          isDev() && console.warn('⚠️ HASH: No productData in rawData');
          return 'unknown';
        }

        // Three-state: absent must stay distinguishable from false.
        const availableToken = (v) => (v === true ? '1' : v === false ? '0' : '?');
        // Total order (returns 0 on equal ids) so sort() is deterministic.
        const byId = (a, b) => {
          const x = String(a?.id);
          const y = String(b?.id);
          return x < y ? -1 : x > y ? 1 : 0;
        };

        const addOns = productData.add_on_products || [];
        const parts = [];

        for (const p of addOns.slice().sort(byId)) {
          // The 3D fields are CONTENT that the render pipeline rewrites on its own
          // schedule (a re-bake swaps a product's GLB or a variant's baked texture).
          // Leaving them out of the fingerprint meant a returning visitor kept the
          // old model/texture URLs until the 7-day TTL, whatever they refreshed.
          let segment = `${p?.id}:${p?.price}:${p?.glb_url || ''}:${p?.is_inset === true ? 'i' : ''}`;
          for (const v of (p?.variants || []).slice().sort(byId)) {
            segment +=
              `,${v?.id}:${v?.price}:${availableToken(v?.available)}` +
              `:${v?.configurator_3d_albedo_url || ''}` +
              // Per-variant GLBs land when a render batch is APPROVED, after deploy,
              // so only the fingerprint (not PPD_CONTRACT_VERSION) can bust for them.
              `:${v?.variant_glb_url || ''}:${v?.variant_inset_glb_url || ''}`;
          }
          parts.push(segment);
        }

        // The base product's OWN variants — the ids-only hash could not see these
        // at all, so a re-price of the door itself was invisible.
        for (const v of (productData.variants || []).slice().sort(byId)) {
          parts.push(`b${v?.id}:${v?.price}:${availableToken(v?.available)}`);
        }

        // Lead time single source of truth: the base product's
        // `lead_time_category` metaobject and the transit-days theme setting.
        // Baked into the cached PPD by Layer 4 (baseProduct.lead_time /
        // shipping_transit_days), so without this a metaobject edit (e.g.
        // 1-3 -> 2-4 weeks) stayed invisible to returning visitors until the
        // 7-day TTL. Churn: owner edits, a handful per year -- the exception the
        // "add a field only with its churn" rule above allows.
        const ltc = productData.lead_time_category;
        const tt = productData.shipping_transit_days;
        if (ltc || tt) {
          parts.push(
            `lt${ltc?.unfinished?.low ?? ''}-${ltc?.unfinished?.high ?? ''}` +
              `/${ltc?.finished?.low ?? ''}-${ltc?.finished?.high ?? ''}` +
              `:tt${tt?.low ?? ''}-${tt?.high ?? ''}`
          );
        }

        const fingerprint = parts.join('|');

        // Same 32-bit rolling hash as before — a cache-validation digest, not a
        // security boundary. Collision risk is per-change (~2^-32), not birthday.
        let hash = 0;
        for (let i = 0; i < fingerprint.length; i++) {
          hash = (hash << 5) - hash + fingerprint.charCodeAt(i);
          hash = hash & hash; // Convert to 32bit integer
        }

        const result = `v2_${addOns.length}_${hash}`;
        isDev() &&
          console.log(`🔒 HASH: Generated from pre-extracted data (${result})`);
        return result;
      } catch (error) {
        isDev() &&
          console.warn('⚠️ HASH: Generation failed from rawData:', error);
        return 'error';
      }
    },

    /**
     * PPD-FIRST ENFORCEMENT: This method is PROHIBITED
     * Use generateProductHashFromData(rawData) which accepts pre-extracted data
     *
     * @throws {Error} Always throws - enforces architectural purity
     * @deprecated Use generateProductHashFromData(rawData) instead
     */
    generateProductHash() {
      throw new Error(
        'CRITICAL: generateProductHash() is PROHIBITED in optimized architecture. ' +
          'Use generateProductHashFromData(rawData) with pre-extracted data from extractConfigurationData(). ' +
          'This eliminates redundant DOM reads and improves performance by 1-2ms. ' +
          'See TASKS-INITIALIZER-OPTIMIZATION.md for migration guide.'
      );
    },

    /**
     * Wait for a module dependency to be available before proceeding
     * Prevents race conditions with ES6 module async loading
     *
     * NOTE: This is a workaround for the legacy global namespace and async script loading.
     * If this project is migrated to a modern module bundler (Webpack, Vite, etc.),
     * this function should be replaced with standard ES module imports.
     *
     * @param {string} moduleName - Name of module in MurphyDoorConfigurator namespace
     * @param {number} timeoutMs - Maximum wait time in milliseconds
     * @returns {Promise<boolean>} - True if module loaded, false if timeout
     */
    waitForModuleDependency: function (moduleName, timeoutMs = 5000) {
      return new Promise((resolve) => {
        const startTime = Date.now();
        const checkInterval = 200; // Reduced polling frequency (75% fewer checks, negligible UX impact)

        const check = () => {
          if (
            window.MurphyDoorConfigurator &&
            window.MurphyDoorConfigurator[moduleName]
          ) {
            const elapsed = Date.now() - startTime;
            isDev() &&
              console.log(`✅ DEPENDENCY: ${moduleName} loaded (${elapsed}ms)`);
            resolve(true);
            return;
          }

          if (Date.now() - startTime > timeoutMs) {
            isDev() &&
              console.error(
                `❌ DEPENDENCY: ${moduleName} failed to load within ${timeoutMs}ms`
              );
            resolve(false);
            return;
          }

          setTimeout(check, checkInterval);
        };

        check();
      });
    },

    /**
     * Wait for multiple module dependencies to be available before proceeding
     * Uses Promise.all to wait for all modules concurrently
     *
     * @param {string[]} moduleNames - Array of module names in MurphyDoorConfigurator namespace
     * @param {number} timeoutMs - Maximum wait time in milliseconds
     * @returns {Promise<boolean>} - True if all modules loaded, false if any timeout
     */
    waitForModuleDependencies: function (moduleNames, timeoutMs = 5000) {
      const promises = moduleNames.map((name) =>
        this.waitForModuleDependency(name, timeoutMs)
      );
      return Promise.all(promises).then((results) => results.every(Boolean));
    },
  };

  // CRITICAL FIX: Guard against Shopify async section rendering re-loading this script.
  // When Shopify re-injects the script tag (e.g., UIUpdater or DataPipeline sections load late),
  // the entire IIFE re-executes, creating a FRESH initializer object with _isInitialized=false.
  // This guard preserves the original initializer if it already exists, preventing double bootstrap.
  // Guard v3: Use window-level flag (survives namespace recreation) PLUS namespace check.
  if (
    window.__murphyDoorBootstrapComplete ||
    window.MurphyDoorConfigurator.initializer
  ) {
    isDev() &&
      console.log(
        '🛡️ INITIALIZER: Script re-loaded (async section rendering) - bootstrap already complete, skipping duplicate run'
      );
  } else {
    window.MurphyDoorConfigurator.initializer = initializer;

    // Initialize when DOM is ready - only runs for the FIRST script load
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        initializer.initialize();
      });
    } else {
      initializer.initialize();
    }
  }
})(window);
